import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import get from 'lodash/get';
import partialRight from 'lodash/partialRight';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import LoginHeading from './LoginHeading';
import Loader from './LoginLoader';
import { changePassword, discardAccesses } from '../../../actions/AuthActions';
import { getUITA } from '../../../utils/commonUtils';
import FormHelperText from '../Form/FormHelperText';
import PasswordInput from '../Form/PasswordInput';

const useStyles = makeStyles({
  button: {
    width: '100%',
    '& > div': {
      overflow: 'hidden',
    },
  },
});

const selectAuth = partialRight(get, 'auth', {});

export const selectPasswordChangeError = createSelector(
  selectAuth,
  partialRight(get, 'passwordChangeError', {})
);

export const selectUsername = createSelector(selectAuth, partialRight(get, 'userLogonCode', ''));

export const selectPending = createSelector(selectAuth, partialRight(get, 'pending', false));

export const selectAuthError = createSelector(selectPasswordChangeError, (passwordChangeError) => {
  const message = get(passwordChangeError, 'message', '');
  const status = get(passwordChangeError, 'status', '');

  return `Error: ${message}${status ? ` Code: ${status}` : ''}`;
});

export const selectHasError = createSelector(
  selectPasswordChangeError,
  (passwordChangeError) => !!passwordChangeError
);

const LoginPasswordChangeForm = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const authError = useSelector(selectAuthError);
  const hasError = useSelector(selectHasError);
  const username = useSelector(selectUsername);
  const pending = useSelector(selectPending);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const disableSubmitButton =
    newPassword !== confirmPassword || !(currentPassword && newPassword && confirmPassword);

  useEffect(() => {
    dispatch(discardAccesses());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleValueChange = (setState) => ({ target: { value } }) => setState(value);

  const handlePasswordChangeClick = useCallback(() => {
    dispatch(
      changePassword({
        Username: username,
        OldPassword: currentPassword,
        NewPassword: newPassword,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPassword, newPassword, username, changePassword]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      handlePasswordChangeClick();
    },
    [handlePasswordChangeClick]
  );

  return (
    <div className="login-form">
      <LoginHeading text="Password Change" />
      <form onSubmit={handleSubmit} ui-ta={getUITA('password-change-form')}>
        <span id="password" ui-ta="password" className="login-input">
          <PasswordInput
            error={hasError}
            inputFontSize="1rem"
            label="Current Password"
            type="password"
            value={currentPassword}
            width="100%"
            handleChange={handleValueChange(setCurrentPassword)}
          />
        </span>
        <span id="password" ui-ta="password" className="login-input">
          <PasswordInput
            error={hasError}
            inputFontSize="1rem"
            label="New Password"
            type="password"
            value={newPassword}
            width="100%"
            handleChange={handleValueChange(setNewPassword)}
          />
        </span>
        <span id="password" ui-ta="password" className="login-input">
          <PasswordInput
            error={hasError}
            inputFontSize="1rem"
            label="Confirm Password"
            type="password"
            value={confirmPassword}
            width="100%"
            handleChange={handleValueChange(setConfirmPassword)}
          />
        </span>
        {hasError && (
          <FormHelperText id="statusMessage" error>
            {authError}
          </FormHelperText>
        )}
        <Button
          className={classes.button}
          color="primary"
          id="login-btn"
          onClick={handlePasswordChangeClick}
          disabled={disableSubmitButton}
          ui-ta="login-btn"
          variant="primary"
        >
          {pending ? <Loader /> : 'Change Password'}
        </Button>
        <input type="submit" hidden />
      </form>
    </div>
  );
};

export default LoginPasswordChangeForm;
